// PhotoSwipeCore.js

import { useEffect } from "preact/hooks";
import PhotoSwipeLightbox from 'photoswipe/lightbox';
//import 'photoswipe/style.css';

import {
  selector,
  anti_selector,
  infos_to_display,
  backdrop_opacity,
  animation_toggle,
  animation_speed,
  animation_speeds,
  right_click_protection,
  slideshow,
  download,
  deep_linking,
  deep_linking_slug,
  social_sharing,
  map as location_map,
} from "../settings";

import {
  transformMwlData,
  transformAnimationType,
} from './utils';

import { initCaption } from './initCaption';
import { initSlideshow } from './initSlideshow';
import { initDownloadButton } from './initDownloadButton';
import { initRightClickProtection } from './initRightClickProtection';
import { initDeepLinking } from './initDeepLinking';
import { initSocialSharing } from './initSocialSharing';
import { initMapFunctionality } from './initMapFunctionality';

const mwlNamespace = 'mwl_pswp';

export default function PhotoSwipeCore() {

  useEffect(() => {
    // Dynamically import PhotoSwipe CSS only if it hasn't been loaded already (photoswipe/style.css)
    if (!document.querySelector('link[href*="photoswipe"]')) {
      console.warn('(Meow Lightbox PS) PhotoSwipe CSS not found. Importing it dynamically.');
      import(/* webpackChunkName: "photoswipe-css" */ 'photoswipe/style.css');
    }


    let transformedData = transformMwlData(window.mwl_data);
    if (!transformedData.length) {
      console.warn('(Meow Lightbox PS) No images found.');
      return;
    }

  
    const antiClasses = anti_selector.split(',').map(className => `${className.trim()} img`);
    const fullAntiselector = antiClasses.join(', ');

    const ignoredImages = document.querySelectorAll(fullAntiselector);
    const ignoredIds = Array.from(ignoredImages).map(image => image.dataset.mwlImgId || image.className.match(/wp-image-(\d+)/)?.[1]);

    transformedData = transformedData.filter(item => !ignoredIds.includes('' + item.id));

    let lightbox;
    try {
      lightbox = new PhotoSwipeLightbox({
        bgOpacity: backdrop_opacity / 100,
        mainClass: 'mwl',
        pswpClassName: mwlNamespace,
        dataSource: transformedData,
        showHideAnimationType: transformAnimationType(animation_toggle),
        showAnimationDuration: animation_speeds[animation_speed],
        hideAnimationDuration: animation_speeds[animation_speed],
        pswpModule: () => import(/* webpackChunkName: "photoswipe" */ 'photoswipe'),
      });

      // Filters for thumbEl and placeholderSrc
      lightbox.addFilter('thumbEl', (thumbEl, data) => {
        let el = document.querySelector(`[data-mwl-img-id='${data.id}']`) || document.querySelector(`.wp-image-${data.id}`);
        return el || thumbEl;
      });

      lightbox.addFilter('placeholderSrc', (placeholderSrc, slide) => {
        let el = document.querySelector(`[data-mwl-img-id='${slide.data.id}']`) || document.querySelector(`.wp-image-${slide.data.id}`);
        return el ? el.src : placeholderSrc;
      });

      // Initialize features
      initCaption(lightbox, infos_to_display);
      if (slideshow.enabled) initSlideshow(lightbox, slideshow);
      if (download.enabled) initDownloadButton(lightbox);
      if (right_click_protection) initRightClickProtection(lightbox);
      if (deep_linking) initDeepLinking(lightbox, transformedData, deep_linking_slug);
      if (social_sharing) initSocialSharing(lightbox);
      if (location_map.enabled) initMapFunctionality(lightbox);

      lightbox.init();
      window.pswpLightbox = lightbox;

      // Attach click event listeners to images
      const classes = selector.split(',').map(className => `${className.trim()} img`);
      const fullSelector = classes.join(', ');
      const images = document.querySelectorAll(fullSelector);

      const imagesDiff = Array.from(images).filter(image => !Array.from(ignoredImages).includes(image));
      imagesDiff.forEach((img) => {
        let id = img.dataset.mwlImgId || img.className.match(/wp-image-(\d+)/)?.[1];
        if (id) {
          const index = transformedData.findIndex(item => item.id == id);
          img.style.cursor = 'zoom-in';
          img.addEventListener('click', (event) => {
            event.preventDefault();
            lightbox.loadAndOpen(index);
          });
        }
      });
    } catch (error) {
      console.error("Error initializing PhotoSwipe:", error);
    }

    return () => {
      if (lightbox) {
        lightbox.destroy();
        lightbox = null;
      }
    };
  }, []);

  return null;
}